// src/pages/InternalInbox.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { internalApi } from "../api/internal";
import { useAuth } from "../auth/AuthContext";
import { useRealtime } from "../realtime/RealtimeProvider";
import PickParticipantsModal from "../components/internal/PickParticipantsModal";

export default function InternalInbox() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();

  const { user } = useAuth();
  const rt = useRealtime();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toasts, setToasts] = useState([]);

  // NUEVO: modal seleccionar participantes al crear
  const [pickOpen, setPickOpen] = useState(false);
  const [newThreadId, setNewThreadId] = useState(null);

  const type = sp.get("type") || "all"; // all|general|lead|contact|closed
  const q = sp.get("q") || "";

  function showToast(message) {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  }

  function requestBrowserAlerts() {
    if (!("Notification" in window)) {
      showToast("Este navegador no soporta notificaciones.");
      return;
    }
    if (Notification.permission === "granted") {
      showToast("Alertas activadas.");
      return;
    }
    Notification.requestPermission().then((permission) => {
      showToast(permission === "granted" ? "Alertas activadas." : "Alertas no activadas.");
    });
  }

  async function fetchThreads() {
    setLoading(true);
    try {
      const { data } = await internalApi.listThreads({ type, q });
      setRows(data?.rows || []);
    } catch (e) {
      console.error("internal listThreads", e);
      showToast(e?.response?.data?.error || "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, q]);

  useEffect(() => {
    if (!rt?.onEvent) return;

    return rt.onEvent((evt) => {
      if (evt.type !== "internal.notify" && evt.type !== "internal.message.new") return;

      const payload = evt.payload || {};
      const event = payload.event || "";
      const threadId = payload.threadId || payload.thread?.id || payload.thread_id;
      const threadTitle =
        payload.thread?.title ||
        (payload.thread?.lead_id ? `Lead #${payload.thread.lead_id}` : "") ||
        (threadId ? `Conversación #${threadId}` : "Chat interno");

      if (evt.type === "internal.notify") {
        fetchThreads();

        const actorUserId = payload.actorUserId ?? payload.actor_user_id ?? null;
        if (actorUserId != null && user?.id != null && String(actorUserId) === String(user.id)) {
          return;
        }

        const msg =
          event === "thread_assigned"
            ? `Te agregaron a ${threadTitle}`
            : event === "thread_removed"
            ? `Te quitaron de ${threadTitle}`
            : event === "thread_created"
            ? `Nueva conversación interna: ${threadTitle}`
            : "Actualización en chat interno";

        showToast(msg);

        if ("Notification" in window && Notification.permission === "granted" && document.hidden) {
          new Notification("Chat interno", { body: msg });
        }
      }

      if (evt.type === "internal.message.new") {
        fetchThreads();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rt]);

  const filters = [
    ["all", "Todas"],
    ["general", "Generales"],
    ["lead", "Por Lead"],
    ["contact", "Por Contacto"],
    ["closed", "Cerradas"],
  ];

  const titleOf = (t) =>
    t.title ||
    (t.thread_type === "general"
      ? "Conversación general"
      : t.lead_id
      ? `Lead #${t.lead_id}`
      : t.contact_id
      ? `Contacto #${t.contact_id}`
      : "Conversación");

  const subtitleOf = (t) => {
    const last = (t.last_message || "").trim();
    if (!last) return "Sin mensajes";
    return last.length > 90 ? last.slice(0, 90) + "…" : last;
  };

  const badgeText = (t) => {
    if (t.lead_id) return "LEAD";
    if (t.contact_id) return "CONTACT";
    return "GENERAL";
  };

  const badgeLetter = (t) => {
    const txt = badgeText(t);
    return txt === "LEAD" ? "L" : txt === "CONTACT" ? "C" : "G";
  };

  // ==========================================================
  // CREAR THREAD + ELEGIR PARTICIPANTES (NUEVO)
  // ==========================================================
  const onCreate = async () => {
    try {
      const { data } = await internalApi.createThread({ title: "Conversación general" });
      const id = data?.thread?.id;
      if (!id) {
        showToast("No se pudo crear");
        return fetchThreads();
      }
      setNewThreadId(id);
      setPickOpen(true); // abrir selector de usuarios
      showToast("Conversación creada");
    } catch (e) {
      showToast(e?.response?.data?.error || "No se pudo crear");
    }
  };

  const onConfirmUsers = async (userIds) => {
    const id = newThreadId;
    setPickOpen(false);
    if (!id) return;

    try {
      // Agregar participantes seleccionados
      for (const uid of userIds) {
        await internalApi.addParticipant(id, {
          user_id: uid,
          role: "agent",
          can_post: 1,
          can_manage: 0,
          can_reply_customer: 0,
        });
      }
    } catch (e) {
      console.error(e);
      showToast(e?.response?.data?.error || "No se pudieron agregar participantes");
    } finally {
      navigate(`/internal/${id}`);
    }
  };

  const sorted = useMemo(() => {
    return [...(rows || [])].sort((a, b) => {
      const ta = new Date(a.last_at || a.created_at).getTime();
      const tb = new Date(b.last_at || b.created_at).getTime();
      return tb - ta;
    });
  }, [rows]);

  const colorFor = (t) => {
    if (t.lead_id) return "#60a5fa"; // azul
    if (t.contact_id) return "#a78bfa"; // violeta
    return "#94a3b8"; // gris
  };

  const statusChip = (t) => {
    const s = String(t.status || "").toLowerCase();
    if (s === "open") return { bg: "rgba(34,197,94,.12)", bd: "rgba(34,197,94,.25)", tx: "#86efac" };
    if (s === "closed") return { bg: "rgba(148,163,184,.10)", bd: "rgba(148,163,184,.18)", tx: "#cbd5e1" };
    return { bg: "rgba(245,158,11,.12)", bd: "rgba(245,158,11,.22)", tx: "#fcd34d" };
  };

  const onSetType = (k) => {
    setSp((cur) => {
      cur.set("type", k);
      return cur;
    });
  };

  const onSetQ = (val) => {
    setSp((cur) => {
      cur.set("q", val);
      return cur;
    });
  };

  return (
    <div className="internal-root">
      <style>{css}</style>

      <div className="topbar">
        <div className="topbar-row">
          <div className="head">
            <div className="title">Chat Interno</div>
            <div className="sub">Conversaciones internas por equipo</div>
          </div>

          <button className="btn-new" onClick={onCreate}>
            + Nueva conversación
          </button>
          <button className="btn-alerts" onClick={requestBrowserAlerts}>
            Activar alertas
          </button>
        </div>

        <div className="topbar-row">
          <div className="filters">
            {filters.map(([k, label]) => {
              const on = type === k;
              return (
                <button
                  key={k}
                  type="button"
                  className={`pill ${on ? "on" : ""}`}
                  onClick={() => onSetType(k)}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div className="search">
            <svg viewBox="0 0 24 24" className="icon">
              <path
                d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15z"
                stroke="currentColor"
                strokeWidth="1.7"
                fill="none"
                strokeLinecap="round"
              />
            </svg>
            <input
              className="input"
              placeholder="Buscar por título o último mensaje…"
              value={q}
              onChange={(e) => onSetQ(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="list">
        {loading ? (
          <div className="panel">
            <div className="empty">Cargando…</div>
          </div>
        ) : !sorted.length ? (
          <div className="panel">
            <div className="empty">No hay conversaciones.</div>
          </div>
        ) : (
          <div className="panel">
            {sorted.map((t) => {
              const chip = statusChip(t);
              return (
                <button
                  key={t.id}
                  className="row"
                  onClick={() => navigate(`/internal/${t.id}`)}
                  title={titleOf(t)}
                >
                  <div
                    className="avatar"
                    style={{ borderColor: `${colorFor(t)}55`, background: `${colorFor(t)}14`, color: "#e5e7eb" }}
                  >
                    {badgeLetter(t)}
                  </div>

                  <div className="body">
                    <div className="line1">
                      <div className="name">{titleOf(t)}</div>
                      <span
                        className="status"
                        style={{ background: chip.bg, borderColor: chip.bd, color: chip.tx }}
                      >
                        {String(t.status || "").toUpperCase()}
                      </span>
                      <span className="tag" style={{ borderColor: `${colorFor(t)}40`, color: "#cbd5e1" }}>
                        {badgeText(t)}
                      </span>
                    </div>

                    <div className="line2">{subtitleOf(t)}</div>
                  </div>

                  <div className="time">
                    {t.last_at ? new Date(t.last_at).toLocaleString() : ""}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="toast-container">
        {toasts.map((t) => (
          <div key={t.id} className="toast">
            {t.message}
          </div>
        ))}
      </div>

      {/* ======================================================
          MODAL NUEVO: seleccionar usuarios participantes
         ====================================================== */}
      <PickParticipantsModal
        open={pickOpen}
        excludeUserId={user?.id}
        onClose={() => {
          setPickOpen(false);
          if (newThreadId) navigate(`/internal/${newThreadId}`);
        }}
        onConfirm={onConfirmUsers}
      />
    </div>
  );
}

/* -------------------- CSS in-file (alineado a LeadsBoard) -------------------- */
const css = `
.internal-root{
  height: calc(100vh - 80px);
  padding: 10px;
  display:grid;
  grid-template-rows:auto 1fr;
  gap:10px;
  color:#e5e7eb;
  background:#0b1220;
}

.topbar{ display:flex; flex-direction:column; gap:10px; }
.topbar-row{ display:flex; gap:10px; align-items:center; flex-wrap:wrap; }

.head{ display:flex; flex-direction:column; gap:2px; }
.title{ font-weight:900; letter-spacing:.2px; }
.sub{ color:#94a3b8; font-size:12px; }

.input{
  background: rgba(255,255,255,.04);
  border:1px solid rgba(255,255,255,.08);
  border-radius:10px;
  padding:8px 12px;
  color:#e5e7eb;
  height: 40px;
  display:flex;
  align-items:center;
}
.input:focus{
  outline:none;
  border-color: rgba(59,130,246,.45);
  box-shadow:0 0 0 3px rgba(59,130,246,.18);
}

.btn-new{
  margin-left:auto;
  background:#3b82f6;
  border:1px solid #2563eb;
  color:white;
  padding:8px 16px;
  border-radius:10px;
  font-weight:800;
  cursor:pointer;
  white-space:nowrap;
}
.btn-new:hover{ background:#2563eb; }
.btn-alerts{
  background:rgba(255,255,255,.06);
  border:1px solid rgba(255,255,255,.10);
  color:#e5e7eb;
  padding:8px 12px;
  border-radius:8px;
  font-weight:800;
  cursor:pointer;
  white-space:nowrap;
}
.btn-alerts:hover{ background:rgba(59,130,246,.15); border-color:rgba(59,130,246,.35); }

.search{
  position: relative;
  flex: 1;
  min-width: 280px;
  display: flex;
  align-items: center;
  margin-left:auto;
}
.search .icon{
  position:absolute;
  left:12px;
  top:50%;
  transform:translateY(-50%);
  width:16px;
  height:16px;
  color:#94a3b8;
  pointer-events:none;
}
.search .input{
  width: 100%;
  padding-left: 40px;
  padding-right: 12px;
  line-height: 40px;
}

.filters{ display:flex; gap:8px; flex-wrap:wrap; }
.pill{
  border:1px solid rgba(255,255,255,.08);
  background: rgba(255,255,255,.04);
  padding:6px 10px;
  border-radius:999px;
  color:#cfd8ef;
  font-size:12px;
  cursor:pointer;
}
.pill:hover{ background: rgba(255,255,255,.06); }
.pill.on{
  background: rgba(59,130,246,.12);
  border-color: rgba(59,130,246,.35);
  color:#e5e7eb;
  font-weight:800;
}

.list{ overflow:auto; }
.panel{
  background:#0f172a;
  border:1px solid rgba(255,255,255,.08);
  border-radius:12px;
  overflow:hidden;
}

.row{
  width:100%;
  text-align:left;
  padding:12px;
  display:flex;
  gap:12px;
  align-items:flex-start;
  background:transparent;
  border:none;
  cursor:pointer;
  border-bottom:1px solid rgba(255,255,255,.06);
}
.row:last-child{ border-bottom:none; }
.row:hover{ background: rgba(255,255,255,.04); }

.avatar{
  width:40px; height:40px;
  border-radius:999px;
  border:1px solid rgba(255,255,255,.15);
  display:grid;
  place-items:center;
  font-weight:900;
  flex: 0 0 auto;
}

.body{ flex:1; min-width:0; }
.line1{ display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.name{ font-weight:900; color:#e7eaf3; max-width: 58ch; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.status{
  font-size:10px;
  font-weight:900;
  padding:2px 8px;
  border-radius:999px;
  border:1px solid rgba(255,255,255,.12);
}
.tag{
  font-size:10px;
  font-weight:900;
  padding:2px 8px;
  border-radius:999px;
  border:1px solid rgba(255,255,255,.12);
  background: rgba(255,255,255,.04);
}

.line2{ margin-top:4px; color:#cbd5e1; font-size:12px; opacity:.92; max-width: 90ch; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

.time{
  color:#94a3b8;
  font-size:11px;
  white-space:nowrap;
  margin-left:auto;
  padding-top:2px;
}

.empty{ color:#94a3b8; font-size:12px; padding:14px; text-align:center; }

.toast-container{ position: fixed; right: 20px; bottom: 20px; display: flex; flex-direction: column; gap: 10px; z-index: 99999; }
.toast{
  background: rgba(59,130,246,0.95);
  padding: 14px 18px; color: white; font-weight: 700; border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.15); box-shadow: 0 8px 20px rgba(0,0,0,0.4);
  animation: toast-in 0.35s ease-out, toast-out 0.6s ease-in 2.6s forwards;
  font-size: 14px; min-width: 240px;
}
@keyframes toast-in { 0% { transform: translateY(20px) scale(0.95); opacity: 0; } 100% { transform: translateY(0) scale(1); opacity: 1; } }
@keyframes toast-out { 0% { transform: translateY(0) scale(1); opacity: 1; } 100% { transform: translateY(20px) scale(0.9); opacity: 0; } }
`;
